import { types } from "@gadgetinc/mobx-quick-tree";
import { AppSettings } from "../AppSettings";
import { ModelField } from "../database/ModelField";
import { getAppSettings, getGadgetEnv } from "../utils";

export enum FieldType {
  /**
   * Any value at all.
   *
   * @deprecated Prefer {@linkcode FieldType.JSON} where possible, it's more descriptive. This can't be removed until we update all Anys in the DB.
   */
  Any = "Any",

  Array = "Array",
  BelongsTo = "BelongsTo",
  Boolean = "Boolean",

  /** @deprecated unused */
  Code = "Code",

  Color = "Color",
  Computed = "Computed",
  DateTime = "DateTime",
  Email = "Email",
  EncryptedString = "EncryptedString",
  Enum = "Enum",
  File = "File",
  HasMany = "HasMany",
  HasManyThrough = "HasManyThrough",
  HasOne = "HasOne",
  ID = "ID",
  JSON = "JSON",
  Money = "Money",

  /** @deprecated unused */
  Null = "Null",

  Number = "Number",
  Object = "Object",
  Password = "Password",
  RecordState = "RecordState",
  RichText = "RichText",
  RoleAssignments = "RoleAssignments",
  String = "String",
  URL = "URL",
  Vector = "Vector",
}

export const AllFieldTypes = Object.values(FieldType);

export const StoredFieldTypes = [
  FieldType.Any,
  FieldType.Array,
  FieldType.BelongsTo,
  FieldType.Boolean,
  FieldType.Color,
  FieldType.DateTime,
  FieldType.Email,
  FieldType.EncryptedString,
  FieldType.Enum,
  FieldType.File,
  FieldType.ID,
  FieldType.JSON,
  FieldType.Money,
  FieldType.Number,
  FieldType.Password,
  FieldType.RecordState,
  FieldType.RichText,
  FieldType.RoleAssignments,
  FieldType.String,
  FieldType.URL,
  FieldType.Vector,
] as const;

export type StoredFieldType = (typeof StoredFieldTypes)[number];
export type StoredField = ModelField & { fieldType: StoredFieldType };

export const isStoredFieldType = (fieldType: FieldType): fieldType is StoredFieldType => {
  return StoredFieldTypes.includes(fieldType as any);
};

export const isStoredField = (field: ModelField): field is StoredField => {
  return isStoredFieldType(field.fieldType);
};

const SearchableFieldTypes = [
  FieldType.Any,
  FieldType.Boolean,
  FieldType.BelongsTo,
  FieldType.Color,
  FieldType.DateTime,
  FieldType.Email,
  FieldType.Enum,
  FieldType.HasMany,
  FieldType.HasManyThrough,
  FieldType.HasOne,
  FieldType.ID,
  FieldType.JSON,
  FieldType.Money,
  FieldType.Number,
  FieldType.String,
  FieldType.RecordState,
  FieldType.RichText,
  FieldType.RoleAssignments,
  FieldType.URL,
] as const;

const RelationshipSearchableFieldTypes = [FieldType.HasOne, FieldType.HasMany, FieldType.HasManyThrough] as const;

export type SearchableFieldType = Exclude<(typeof SearchableFieldTypes)[number], (typeof RelationshipSearchableFieldTypes)[number]>;
export type FieldWithSearchableType = ModelField & { fieldType: SearchableFieldType };

export const isSearchableFieldType = (fieldType: FieldType, settings: AppSettings): fieldType is SearchableFieldType => {
  const searchable = SearchableFieldTypes.includes(fieldType as any);

  if (settings.frameworkVersion.lessThan("1.5.0") || !getGadgetEnv(settings).flags.features.relatedModelSearch) {
    return searchable && !RelationshipSearchableFieldTypes.includes(fieldType as any);
  } else {
    return searchable;
  }
};

export const hasSearchableFieldType = (field: ModelField): field is FieldWithSearchableType => {
  return isSearchableFieldType(field.fieldType, getAppSettings(field));
};

const FilterableTypes = [
  FieldType.Any,
  FieldType.ID,
  FieldType.String,
  FieldType.Number,
  FieldType.Boolean,
  FieldType.URL,
  FieldType.Email,
  FieldType.JSON,
  FieldType.Money,
  FieldType.DateTime,
  FieldType.Color,
  FieldType.Vector,
  FieldType.RichText,
  FieldType.RecordState,
  FieldType.RoleAssignments,
  FieldType.Enum,
  FieldType.BelongsTo,
  FieldType.HasMany,
  FieldType.HasOne,
  FieldType.HasManyThrough,
] as const;

const NonFilterableTypesPriorTo1_3_0 = [FieldType.HasMany, FieldType.HasOne, FieldType.HasManyThrough];

export type FilterableFieldType = (typeof FilterableTypes)[number];
export type FieldWithFilterableType = ModelField & { fieldType: FilterableFieldType };

export const isFilterableFieldType = (fieldType: FieldType, settings: AppSettings): fieldType is FilterableFieldType => {
  const isFilterable = FilterableTypes.includes(fieldType as any);

  if (fieldType === FieldType.Computed) {
    return getGadgetEnv(settings).flags.features.computedFieldFiltering;
  } else if (settings.frameworkVersion.greaterThanOrEqual("1.3.0")) {
    return isFilterable;
  } else {
    return isFilterable && !NonFilterableTypesPriorTo1_3_0.includes(fieldType);
  }
};

export const hasFilterableFieldType = (field: ModelField): field is FieldWithFilterableType => {
  return isFilterableFieldType(field.fieldType, getAppSettings(field));
};

const SortableTypes = [
  FieldType.Any,
  FieldType.Boolean,
  FieldType.Color,
  FieldType.DateTime,
  FieldType.Email,
  FieldType.Enum,
  FieldType.ID,
  FieldType.JSON,
  FieldType.Money,
  FieldType.Number,
  FieldType.RecordState,
  FieldType.RichText,
  FieldType.String,
  FieldType.URL,
  FieldType.Vector,
] as const;

export type SortableFieldType = (typeof SortableTypes)[number];
export type FieldWithSortableType = ModelField & { fieldType: SortableFieldType };

export const isSortableFieldType = (fieldType: FieldType, _settings: AppSettings): fieldType is SortableFieldType => {
  return SortableTypes.includes(fieldType as any);
};

export const hasSortableFieldType = (field: ModelField): field is FieldWithSortableType => {
  return isSortableFieldType(field.fieldType, getAppSettings(field));
};

export const validShopifyMetafieldFieldTypes = [
  FieldType.BelongsTo,
  FieldType.Boolean,
  FieldType.Color,
  FieldType.DateTime,
  FieldType.Email,
  FieldType.Enum,
  FieldType.JSON,
  FieldType.Number,
  FieldType.String,
  FieldType.URL,
];

export const SourceControlledFieldTypes = [
  FieldType.BelongsTo,
  FieldType.Boolean,
  FieldType.Color,
  FieldType.Computed,
  FieldType.DateTime,
  FieldType.Email,
  FieldType.EncryptedString,
  FieldType.Enum,
  FieldType.File,
  FieldType.HasMany,
  FieldType.HasManyThrough,
  FieldType.HasOne,
  FieldType.JSON,
  FieldType.Number,
  FieldType.Password,
  FieldType.RichText,
  FieldType.RoleAssignments,
  FieldType.String,
  FieldType.URL,
  FieldType.Vector,
];

export const FieldTypeMSTEnum = types.enumeration<FieldType>(Object.values(FieldType));

// The data that could be used as the title of a record
export const TitleFieldTypes = [FieldType.String, FieldType.URL, FieldType.Email, FieldType.Color, FieldType.ID];

// Data for which there's a natural, meaningful-to-humans string representation available
export const StringFieldTypes = [FieldType.String, FieldType.RichText, FieldType.URL, FieldType.Email, FieldType.Color];

// Data for which there's a numeric, meaningful-to-humans readable numeric representation available
export const NumericFieldTypes = [FieldType.Number, FieldType.Money];

// Data powered by a relationship to a record or records of another model
export const RelationshipFieldTypes = [FieldType.HasManyThrough, FieldType.HasMany, FieldType.HasOne, FieldType.BelongsTo];

// Data powered by a relationship to many records of another model
export const PluralRelationshipFieldTypes = [FieldType.HasManyThrough, FieldType.HasMany];

export const ParentRelationshipFieldTypes = [FieldType.HasManyThrough, FieldType.HasMany, FieldType.HasOne];

// Data types for which the record actually contains and actually owns the data.
// Excludes fields like the Gadget system fields that the developer can't really muck with, and fields like HasMany or Computed, where the value is derived from other data as opposed to a plain old scalar on the record.
export const OwnedFieldTypes = [
  FieldType.Number,
  FieldType.String,
  FieldType.Enum,
  FieldType.RichText,
  FieldType.DateTime,
  FieldType.Email,
  FieldType.URL,
  FieldType.Money,
  FieldType.File,
  FieldType.Color,
  FieldType.Boolean,
  FieldType.JSON,
  FieldType.Any,
  FieldType.BelongsTo,
  FieldType.EncryptedString,
  FieldType.Vector,
  FieldType.RoleAssignments,
];

// Types for data that can be meaningfully differentiated by toggling case sensitivity (ie. when verifying uniqueness)
export const CaseSensitiveFieldTypes = [FieldType.String, FieldType.RichText, FieldType.Email, FieldType.URL];
